package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.remote.safeSnapshots
import com.evoapps.evoshift.data.dto.hiring.JobApplicationDto
import com.evoapps.evoshift.data.dto.hiring.JobPostDto
import com.evoapps.evoshift.data.mapper.HiringMapper
import com.evoapps.evoshift.data.remote.CloudFunctionsClient
import com.evoapps.evoshift.domain.model.ApplicantStatus
import com.evoapps.evoshift.domain.model.CandidateMail
import dev.gitlive.firebase.firestore.FieldValue
import com.evoapps.evoshift.domain.model.CvLink
import com.evoapps.evoshift.domain.model.JobApplication
import com.evoapps.evoshift.domain.model.JobPost
import com.evoapps.evoshift.domain.model.NotFoundException
import com.evoapps.evoshift.domain.repository.HiringRepository
import dev.gitlive.firebase.firestore.Direction
import com.evoapps.evoshift.data.remote.AppFirestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put

/**
 * SH-125 hiring in Firestore: `job_posts/{id}` and `job_posts/{id}/applications/{appId}`, plus the
 * applicationCvLink callable. functions/hiring.js creates the applicants, keeps the counts and
 * deletes what is due; firestore.rules decide who reads and writes.
 */
class HiringRepositoryImpl(
    private val firestore: AppFirestore,
    private val functions: CloudFunctionsClient
) : HiringRepository {

    private val posts get() = firestore.collection("job_posts")
    private fun applications(postId: String) = posts.document(postId).collection("applications")

    override fun observePosts(workplaceId: String, recruiterId: String?): Flow<List<JobPost>> =
        posts.where { "workplaceId" equalTo workplaceId }
            .let { query -> recruiterId?.let { uid -> query.where { "recruiterIds" contains uid } } ?: query }
            .safeSnapshots
            .map { snapshot ->
                snapshot.documents
                    .mapNotNull { doc -> runCatching { HiringMapper.toDomain(doc.data<JobPostDto>(), doc.id) }.getOrNull() }
                    // Newest first (sorted here: no composite index needed).
                    .sortedByDescending { it.createdAt }
            }
            .distinctUntilChanged()

    override suspend fun getPost(postId: String): Result<JobPost> = runCatching {
        val doc = posts.document(postId).get()
        if (!doc.exists) throw NotFoundException("Job post $postId")
        HiringMapper.toDomain(doc.data<JobPostDto>(), doc.id)
    }

    override suspend fun savePost(post: JobPost, isNew: Boolean): Result<String> = runCatching {
        val ref = if (post.id.isBlank()) posts.document else posts.document(post.id)
        // Merged: the server's counts and closedAt stay as they are.
        ref.set(HiringMapper.toWriteDto(post, ref.id), merge = true)
        ref.id
    }

    override suspend fun deletePost(postId: String): Result<Unit> = runCatching {
        posts.document(postId).delete()
    }

    override fun observeApplications(postId: String): Flow<List<JobApplication>> =
        applications(postId)
            .orderBy("createdAt", Direction.DESCENDING)
            .safeSnapshots
            .map { snapshot ->
                snapshot.documents.mapNotNull { doc ->
                    runCatching { HiringMapper.toDomain(doc.data<JobApplicationDto>(), doc.id, postId) }.getOrNull()
                }
            }
            .distinctUntilChanged()

    override suspend fun getApplication(postId: String, applicationId: String): Result<JobApplication> = runCatching {
        val doc = applications(postId).document(applicationId).get()
        if (!doc.exists) throw NotFoundException("Application $applicationId")
        HiringMapper.toDomain(doc.data<JobApplicationDto>(), doc.id, postId)
    }

    override suspend fun updateApplication(
        postId: String,
        applicationId: String,
        status: ApplicantStatus,
        managerNote: String?,
        editorId: String,
        at: String
    ): Result<Unit> = runCatching {
        applications(postId).document(applicationId).updateFields {
            "status" to status.name
            "managerNote" to managerNote
            "updatedAt" to at
            "updatedBy" to editorId
        }
    }

    override suspend fun deleteApplication(postId: String, applicationId: String): Result<Unit> = runCatching {
        applications(postId).document(applicationId).delete()
    }

    override suspend fun setRead(postId: String, applicationId: String, viewerId: String, read: Boolean): Result<Unit> = runCatching {
        applications(postId).document(applicationId).updateFields {
            "readBy" to if (read) FieldValue.arrayUnion(viewerId) else FieldValue.arrayRemove(viewerId)
        }
    }

    override suspend fun setStarred(postId: String, applicationId: String, viewerId: String, starred: Boolean): Result<Unit> = runCatching {
        applications(postId).document(applicationId).updateFields {
            "starredBy" to if (starred) FieldValue.arrayUnion(viewerId) else FieldValue.arrayRemove(viewerId)
        }
    }

    override suspend fun reject(postId: String, applicationId: String, sendMail: Boolean, managerNote: String?): Result<CandidateMail> =
        runCatching {
            val answer = functions.call("rejectApplication", buildJsonObject {
                put("postId", postId)
                put("applicationId", applicationId)
                put("sendMail", sendMail)
                // Absent: the note stays as it is; blank clears it.
                managerNote?.let { put("managerNote", it) }
            }).jsonObject
            InterviewRepositoryImpl.mailOf(answer["mail"]?.jsonPrimitive?.contentOrNull)
        }

    override suspend fun cvLink(postId: String, applicationId: String): Result<CvLink> = runCatching {
        val result = functions.call("applicationCvLink", buildJsonObject {
            put("postId", postId)
            put("applicationId", applicationId)
        }).jsonObject
        fun text(key: String) = result[key]?.jsonPrimitive?.contentOrNull.orEmpty()
        CvLink(url = text("url").ifEmpty { error("No CV link") }, fileName = text("fileName"), expiresAt = text("expiresAt"))
    }
}
